import { useState, useEffect } from 'react';
import { Check, Loader2 } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { SearchableMultiSelect } from '@/components/ui/searchable-multi-select';
import { getMetaPages } from '@/services/api';
import type { MetaPage } from '@/pages/Meta/Connection/types/meta.types';

interface FunnelPageSelectorProps {
    selectedIds: number[];
    onChange: (ids: number[]) => void;
}

export default function FunnelPageSelector({ selectedIds, onChange }: FunnelPageSelectorProps) {
    const [pages, setPages] = useState<MetaPage[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    // null = sem categoria; number = category id
    const [activeCategories, setActiveCategories] = useState<Set<number | null>>(new Set());

    useEffect(() => {
        getMetaPages()
            .then(data => {
                const active = data.filter(p => p.is_active);
                setPages(active);
            })
            .finally(() => setLoading(false));
    }, []);

    const normalize = (s: string) =>
        s.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();

    const categories = Array.from(new Map(
        pages
            .filter(p => p.internal_category_id && p.internal_category_name)
            .map(p => [p.internal_category_id as number, p.internal_category_name as string])
    ).entries()).map(([id, name]) => ({ id, name }));

    const filteredPages = pages.filter(page => {
        const matchesCat =
            activeCategories.size === 0 ||
            activeCategories.has(page.internal_category_id ?? null);
        if (!matchesCat) return false;
        if (!searchTerm.trim()) return true;
        const term = normalize(searchTerm);
        return normalize(page.page_name).includes(term) || page.page_id.includes(searchTerm.trim());
    });

    const isSelected = (id: number) => selectedIds.includes(id);

    const toggle = (page: MetaPage) => {
        if (isSelected(page.id)) {
            onChange(selectedIds.filter(i => i !== page.id));
        } else {
            onChange([...selectedIds, page.id]);
        }
    };


    const allFilteredSelected =
        filteredPages.length > 0 && filteredPages.every(p => isSelected(p.id));

    const handleToggleAll = () => {
        if (allFilteredSelected) {
            const filteredIds = new Set(filteredPages.map(p => p.id));
            onChange(selectedIds.filter(i => !filteredIds.has(i)));
        } else {
            const current = new Set(selectedIds);
            const toAdd = filteredPages.filter(p => !current.has(p.id)).map(p => p.id);
            onChange([...selectedIds, ...toAdd]);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-cyan-500" />
            </div>
        );
    }

    return (
        <div className="space-y-4">
            {/* Contador */}
            {selectedIds.length > 0 && (
                <div className="inline-flex items-center gap-2 bg-cyan-50 text-cyan-800 px-3 py-1.5 rounded-full text-sm font-medium">
                    <Check className="h-4 w-4" />
                    {selectedIds.length} página{selectedIds.length > 1 ? 's' : ''} selecionada{selectedIds.length > 1 ? 's' : ''}
                </div>
            )}

            {/* Busca + Categoria lado a lado */}
            <div className="flex items-end gap-2">
                <div className="flex-1 space-y-1">
                    <Label className="text-xs font-medium">Buscar Página</Label>
                    <Input
                        placeholder="Busque por nome ou ID..."
                        value={searchTerm}
                        onChange={e => setSearchTerm(e.target.value)}
                        className="h-9"
                    />
                </div>
                {categories.length > 0 && (
                <div className="w-52 space-y-1">
                    <Label className="text-xs font-medium text-gray-600">Filtrar por Categoria</Label>
                    <SearchableMultiSelect
                        options={categories.map(c => ({ value: String(c.id), label: c.name }))}
                        values={Array.from(activeCategories).filter(v => v !== null).map(v => String(v))}
                        onValuesChange={vals => {
                            const newIds = new Set(vals.map(Number)) as Set<number | null>;
                            const added = vals.map(Number).filter(id => !activeCategories.has(id));
                            const removed = Array.from(activeCategories)
                                .filter(id => id !== null && !newIds.has(id)) as number[];

                            let updated = [...selectedIds];

                            // Desseleciona páginas das categorias removidas
                            if (removed.length > 0) {
                                const toRemove = new Set(
                                    pages.filter(p => p.internal_category_id !== null && removed.includes(p.internal_category_id as number)).map(p => p.id)
                                );
                                updated = updated.filter(id => !toRemove.has(id));
                            }

                            // Seleciona páginas das categorias adicionadas
                            if (added.length > 0) {
                                const current = new Set(updated);
                                const toAdd = pages
                                    .filter(p => p.internal_category_id !== null && added.includes(p.internal_category_id as number) && !current.has(p.id))
                                    .map(p => p.id);
                                updated = [...updated, ...toAdd];
                            }

                            setActiveCategories(newIds);
                            onChange(updated);
                        }}
                        placeholder="Todas as categorias"
                        searchPlaceholder="Buscar categoria..."
                        showTags={false}
                        selectedLabel={n => `${n} categoria${n > 1 ? 's' : ''} selecionada${n > 1 ? 's' : ''}`}
                    />
                </div>
                )}
                <Button type="button" variant="outline" size="sm" onClick={handleToggleAll} className="whitespace-nowrap h-9">
                    {allFilteredSelected ? 'Desmarcar Todas' : 'Selecionar Todas'}
                </Button>
            </div>

            {/* Lista */}
            <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
                {filteredPages.length === 0 ? (
                    <p className="text-center text-sm text-gray-500 py-6">Nenhuma página encontrada</p>
                ) : (
                    filteredPages.map(page => {
                        const selected = isSelected(page.id);
                        return (
                            <button
                                key={page.id}
                                type="button"
                                onClick={() => toggle(page)}
                                className={`relative w-full p-3 border-2 rounded-xl text-left transition-all hover:border-cyan-400 hover:shadow-sm
                                    ${selected ? 'border-cyan-400 bg-cyan-50/50 shadow-sm' : 'border-gray-200 bg-white'}`}
                            >
                                <div className="flex items-center gap-3">
                                    <div className={`flex-shrink-0 w-5 h-5 rounded border-2 flex items-center justify-center transition-colors
                                        ${selected ? 'bg-cyan-500 border-cyan-500' : 'border-gray-300 bg-white'}`}>
                                        {selected && <Check className="h-3 w-3 text-white" />}
                                    </div>
                                    <div className="w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center flex-shrink-0">
                                        <span className="text-white font-semibold text-sm">
                                            {page.page_name.substring(0, 2).toUpperCase()}
                                        </span>
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2">
                                            <span className="font-medium text-sm text-gray-900 truncate">{page.page_name}</span>
                                            {page.internal_category_name && (
                                                <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-gray-100 text-gray-600 uppercase tracking-wide">
                                                    {page.internal_category_name}
                                                </span>
                                            )}
                                        </div>
                                        <p className="text-xs text-gray-500">ID: {page.page_id}</p>
                                        {page.meta_user_name && (
                                            <p className="text-xs text-gray-400">Conta: {page.meta_user_name}</p>
                                        )}
                                    </div>
                                </div>
                            </button>
                        );
                    })
                )}
            </div>
        </div>
    );
}
